import React, {useRef, useState} from 'react';

import cs from 'classnames';
import Slider from 'react-slick';
import styled from 'styled-components';

import {useIsScreenLargerThan} from 'app/general/mediaqueries';
import {CarouselPreviewElement} from 'app/general/widgets/ImagesCarousel/CarouselPreviewElement';
import {
  CAROUSEL_THUMBNAIL_FULL_SIZE,
  CarouselThumbnail,
} from 'app/general/widgets/ImagesCarousel/CarouselThumbnail';
import {
  StepVideosTitle,
  StyledStepContentSeparatorDiv,
} from 'app/procedures/components/structures';

import type {AttachedMediaWithChangedStateAndEmbed} from 'app/general/widgets/ImagesCarousel/types';

const MAX_NUMBER_OF_THUMBNAILS = 7;

interface Props extends React.ComponentProps<'div'> {
  images: Array<AttachedMediaWithChangedStateAndEmbed>;
}

export function ImagesCarousel({images, className, onClick}: Props) {
  const elementRef = useRef<HTMLDivElement>(null);

  const [currentSlideId, setCurrentSlideId] = useState(0);
  const isXLScreen = useIsScreenLargerThan('xl');
  const displayRef = useRef<Slider>(null);
  const dotMenuRef = useRef<Slider>(null);

  function goToSlideIndex(index: number) {
    if (displayRef.current != null) {
      displayRef.current.slickGoTo(index);
    }
  }

  const imagesToRender = images.filter((image) => image != null);

  return (
    <StyledStepContentSeparatorDiv className={className} onClick={onClick}>
      <StepVideosTitle className="mt-3" />
      <div className="row">
        <div ref={elementRef} className="col-xl-9">
          <Slider
            ref={displayRef}
            centerPadding="0px"
            asNavFor={dotMenuRef.current ?? undefined}
            arrows={false}
            adaptiveHeight={true}
            beforeChange={(_, nextSlide) => setCurrentSlideId(nextSlide)}
          >
            {imagesToRender.map((image, index) => (
              <CarouselPreviewElement
                key={index}
                slideId={index}
                currentSlideId={currentSlideId}
                image={image}
                numberOfShownSlides={Math.min(
                  MAX_NUMBER_OF_THUMBNAILS,
                  imagesToRender.length
                )}
              />
            ))}
          </Slider>
        </div>
        {imagesToRender.length > 1 && (
          <ThumbnailContainer
            isXLScreen={isXLScreen}
            className={cs('col-xl-3', {
              'd-flex justify-content-center': !isXLScreen,
            })}
          >
            <Slider
              ref={dotMenuRef}
              centerPadding="0px"
              centerMode={true}
              asNavFor={displayRef.current || undefined}
              autoplay={false}
              dots={false}
              infinite={true}
              slidesToShow={Math.min(
                MAX_NUMBER_OF_THUMBNAILS,
                imagesToRender.length
              )}
              vertical={isXLScreen}
              arrows={false}
            >
              {imagesToRender.map((image, index) => (
                <CarouselThumbnail
                  key={`carousel-thumbnail-${index}`}
                  image={image}
                  slideId={index}
                  currentSlideId={currentSlideId}
                  onClick={() => goToSlideIndex(index)}
                />
              ))}
            </Slider>
          </ThumbnailContainer>
        )}
      </div>
    </StyledStepContentSeparatorDiv>
  );
}
const ThumbnailContainer = styled.div<{
  isXLScreen: boolean;
}>`
  // CSS structure for the slider:
  //
  //.slick-slider
  //    > .slick-arrow .slick-prev
  //    > .slick-list
  //        > .slick-track
  //            > .slick-slide
  //            > .slick-slide
  //            > .slick-slide
  //            > .slick-slide
  //    > .slick-arrow .slick-next

  ${({isXLScreen}) =>
    !isXLScreen
      ? `
      .slick-slider {
        > .slick-list {
          max-height: ${CAROUSEL_THUMBNAIL_FULL_SIZE}px !important;
          max-width: ${
            MAX_NUMBER_OF_THUMBNAILS * CAROUSEL_THUMBNAIL_FULL_SIZE
          }px !important;
        }
      }
    `
      : ''}

  .slick-slide {
    min-width: ${CAROUSEL_THUMBNAIL_FULL_SIZE}px !important;
    min-height: ${CAROUSEL_THUMBNAIL_FULL_SIZE}px !important;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;
